import { useCallback } from 'react';
import { Input, InputLabel, Toggle, useOptionalField } from '@app/form';
import { useIsAdmin, UnmoderatedTaskLink, API_URL, Session } from 'queries';
import { Button, SquareButton, TooltipText } from '@app/core';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { TagSelectButton } from 'components';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    marginTop: theme.marginBase * 3,
    marginBottom: theme.marginBase * 2,
  },
  inputLine: {
    display: 'flex',
    marginBottom: theme.marginBase,
    gap: theme.marginBase,
  },
  toggle: {
    width: 'auto',
    marginTop: 0,
    marginBottom: 0,
  },
}));

interface Props {
  name: string;
  linkEditableName?: string;
  session: Session;
}

const isDebugEnabled = process.env.NX_DEBUG_TOOLS === 'true';

export const DEFAULT_STUDY_TOOLS_LINKS = [
  {
    studyToolLink: '',
  },
];

export const UnmoderatedTaskLinkInputs = ({
  name,
  session,
  linkEditableName,
}: Props) => {
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const { t } = useTranslation();

  const {
    field: { value = DEFAULT_STUDY_TOOLS_LINKS },
    helpers,
  } = useOptionalField({ name });

  const {
    field: { value: canEditLinks },
  } = useOptionalField({ name: linkEditableName });

  const addLink = useCallback(() => {
    helpers?.setValue([...value, { studyToolLink: '' }]);
  }, [value, helpers]);

  const addTestLink = useCallback(() => {
    helpers?.setValue([
      {
        studyToolLink: `${API_URL}/v2/study/test-redirect/${session.id}?tid={{tid}}`,
      },
    ]);
  }, [helpers, session]);

  const removeLink = useCallback(
    (index: number) => {
      const newLinks = [...value];
      newLinks.splice(index, 1);
      helpers?.setValue(newLinks);
    },
    [value, helpers],
  );

  const onTagChange = (index: number, tags: string[]) => {
    const newLinks = [...value];
    newLinks[index] = {
      ...newLinks[index],
      metadataValues: tags.length ? tags : undefined,
      metadataKey: tags.length ? 'tags' : undefined,
    };
    helpers?.setValue(newLinks);
  };

  const hasSeveralLinks = value.length > 1;

  return (
    <div className={classes.container}>
      <InputLabel
        label={t('editSession.linkConfiguration.studyLink.label')}
        rightAction={
          isAdmin &&
          linkEditableName && (
            <Toggle
              containerClass={classes.toggle}
              name={linkEditableName}
              label="Editable"
              labelBefore
            />
          )
        }
      />
      {value?.map((link: UnmoderatedTaskLink, index: number) => (
        <div className={classes.inputLine}>
          {(hasSeveralLinks || link.metadataValues) && (
            <TagSelectButton
              onChange={(tags: string[]) => onTagChange(index, tags)}
              value={link.metadataValues}
              sessionId={session.id}
              large
            />
          )}
          <TooltipText
            title={
              !canEditLinks
                ? t(
                    'editSession.linkConfiguration.studyLinkConfiguration.linkNotEditable',
                  )
                : undefined
            }
          >
            <Input
              name={`${name}.${index}.studyToolLink`}
              placeholder={t(
                'editSession.linkConfiguration.studyLink.placeholder',
              )}
              noMargin
              disabled={!canEditLinks}
            />
          </TooltipText>
          {hasSeveralLinks && (
            <SquareButton
              type="button"
              color="red"
              variant="transparent"
              icon="ri-delete-bin-line"
              large
              onClick={() => removeLink(index)}
            />
          )}
        </div>
      ))}
      {isAdmin && helpers && (
        <Button
          type="button"
          onClick={addLink}
          small
          color="purple"
          variant="transparent"
        >
          Add link
        </Button>
      )}
      {isAdmin && isDebugEnabled && (
        <Button
          type="button"
          onClick={addTestLink}
          small
          color="purple"
          variant="transparent"
        >
          Lien de test
        </Button>
      )}
    </div>
  );
};
